import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import io from 'socket.io-client';
import './style.css';
import { useToast } from '../ToastContainer';
import TestConnection from './TestConnection';

export default function LiveStreamViewer({ streamId, onClose }) {
  const { user } = useSelector(state => state);
  const { showError } = useToast();
  const currentUserId = user?.id || user?._id;
  
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joined, setJoined] = useState(false);
  const [, setViewers] = useState([]);
  const [streamDuration, setStreamDuration] = useState('00:00:00');
  const [isConnecting, setIsConnecting] = useState(true);
  const [streamConnected, setStreamConnected] = useState(false);
  // True only when decoded video frames are playing — not merely when ICE
  // connects (which previously hid the connecting UI and showed a black box).
  const [hasRemoteVideo, setHasRemoteVideo] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  const [showTestConnection, setShowTestConnection] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  // Start muted so browser autoplay policies allow the video to play
  // immediately on join; viewers can unmute with one click. Without this,
  // Chrome/Safari silently pause the <video> element and viewers see a
  // black screen even though the WebRTC connection is healthy.
  const [isMuted, setIsMuted] = useState(true);
  const [needsUserGestureToPlay, setNeedsUserGestureToPlay] = useState(false);
  const [webrtcSessionKey, setWebrtcSessionKey] = useState(0);
  
  const videoRef = useRef(null);
  const socketRef = useRef(null);
  const peerRef = useRef(null);
  const socketHandlersRef = useRef(null);
  const connectionRetryCount = useRef(0);
  const hasJoinedStreamRoomRef = useRef(false);
  const isHandlingOfferRef = useRef(false);
  const lastHandledOfferSdpRef = useRef('');
  const pendingIceCandidatesRef = useRef([]);
  // Refs that mirror state/props so the WebRTC effect can read latest
  // values without re-running (and creating new RTCPeerConnections).
  const streamRef = useRef(null);
  const retryCountRef = useRef(0);
  const streamConnectedRef = useRef(false);
  const hasRemoteVideoRef = useRef(false);
  const showErrorRef = useRef(showError);
  const onCloseRef = useRef(onClose);
  const MAX_CONNECTION_RETRIES = 3;

  useEffect(() => { streamRef.current = stream; }, [stream]);
  useEffect(() => { retryCountRef.current = retryCount; }, [retryCount]);
  useEffect(() => { streamConnectedRef.current = streamConnected; }, [streamConnected]);
  useEffect(() => { hasRemoteVideoRef.current = hasRemoteVideo; }, [hasRemoteVideo]);
  useEffect(() => { showErrorRef.current = showError; }, [showError]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const markVideoReady = () => {
    if (hasRemoteVideoRef.current) return;
    hasRemoteVideoRef.current = true;
    setHasRemoteVideo(true);
    setStreamConnected(true);
    setIsConnecting(false);
  };

  // Attach an incoming MediaStream to the <video> element and force
  // playback. Browsers block autoplay for audio-bearing media without a
  // user gesture, which previously left viewers staring at a black
  // frame. Starting muted bypasses that policy; if even muted playback
  // is blocked, we surface a "Tap to play" overlay.
  const attachRemoteStream = (remoteStream) => {
    const videoEl = videoRef.current;
    if (!videoEl || !remoteStream) return;
    if (remoteStream.getTracks().length === 0) return;

    if (videoEl.srcObject !== remoteStream) {
      videoEl.srcObject = remoteStream;
    }
    videoEl.muted = true;

    const tryPlay = () => {
      const playPromise = videoEl.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise
          .then(() => {
            setNeedsUserGestureToPlay(false);
            if (videoEl.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
              markVideoReady();
            }
          })
          .catch((err) => {
            console.warn('Autoplay blocked even while muted:', err?.message || err);
            setNeedsUserGestureToPlay(true);
          });
      }
    };

    const onFramesReady = () => {
      markVideoReady();
      tryPlay();
    };

    videoEl.onloadeddata = onFramesReady;
    videoEl.onplaying = onFramesReady;

    if (videoEl.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      onFramesReady();
    } else {
      tryPlay();
    }
  };

  const handleRemoteTrack = (event) => {
    let remoteStream = event.streams && event.streams[0];
    if (!remoteStream && event.track) {
      remoteStream = new MediaStream([event.track]);
    }
    if (remoteStream) {
      attachRemoteStream(remoteStream);
    }
  };

  const drainIceCandidateQueue = async (peer) => {
    if (!peer?.remoteDescription) return;
    const queue = pendingIceCandidatesRef.current.splice(0);
    for (const candidateInit of queue) {
      try {
        await peer.addIceCandidate(new RTCIceCandidate(candidateInit));
      } catch (err) {
        console.warn('Queued ICE candidate failed:', err?.message || err);
      }
    }
  };

  const addRemoteIceCandidate = async (peer, candidateInit) => {
    if (!peer || candidateInit == null) return;
    if (!peer.remoteDescription) {
      pendingIceCandidatesRef.current.push(candidateInit);
      return;
    }
    try {
      await peer.addIceCandidate(new RTCIceCandidate(candidateInit));
    } catch (err) {
      if (!peer.remoteDescription) {
        pendingIceCandidatesRef.current.push(candidateInit);
      } else {
        console.warn('ICE candidate add failed:', err?.message || err);
      }
    }
  };

  // Manual play / unmute handler triggered by user click. A user
  // gesture lifts every browser autoplay restriction.
  const handleManualPlay = async () => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    try {
      videoEl.muted = false;
      await videoEl.play();
      setIsMuted(false);
      setNeedsUserGestureToPlay(false);
      markVideoReady();
    } catch (err) {
      // If unmuted play is still blocked, fall back to muted playback.
      try {
        videoEl.muted = true;
        await videoEl.play();
        setIsMuted(true);
        setNeedsUserGestureToPlay(false);
        markVideoReady();
      } catch (innerErr) {
        console.error('Manual play failed:', innerErr);
      }
    }
  };

  const toggleMute = async () => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    const nextMuted = !videoEl.muted;
    videoEl.muted = nextMuted;
    setIsMuted(nextMuted);
    if (videoEl.paused) {
      try { await videoEl.play(); } catch (_) { /* user can retry */ }
    }
  };


  
  // Calculate stream duration. Depend ONLY on the stream's startedAt so
  // that frequent viewer-count updates (which mutate the stream object
  // reference) do not constantly tear down and recreate this interval —
  // that bug caused the live timer to drift / not tick correctly.
  const startedAt = stream?.startedAt;
  useEffect(() => {
    if (!startedAt) return undefined;

    const tick = () => {
      const startTime = new Date(startedAt).getTime();
      if (Number.isNaN(startTime)) return;
      const diffSec = Math.max(0, Math.floor((Date.now() - startTime) / 1000));
      const hours = Math.floor(diffSec / 3600).toString().padStart(2, '0');
      const minutes = Math.floor((diffSec % 3600) / 60).toString().padStart(2, '0');
      const seconds = (diffSec % 60).toString().padStart(2, '0');
      setStreamDuration(`${hours}:${minutes}:${seconds}`);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Load stream details
  useEffect(() => {
    const loadStream = async () => {
      try {
        const response = await axios.get(`/livestream/${streamId}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        
        if (response.data.success) {
          const liveStream = response.data.liveStream;
          
          // Check if stream is still active
          if (!liveStream.isActive) {
            console.log('Stream is not active');
            // Stream has ended, close viewer without error
            onClose();
            return;
          }
          
          setStream(liveStream);
          setViewers(liveStream.viewers || []);
          setComments(Array.isArray(liveStream.comments) ? liveStream.comments : []);
        }
      } catch (error) {
        console.error('Error loading stream:', error);
        
        // Handle 404 gracefully - stream doesn't exist or has ended
        if (error.response?.status === 404) {
          console.log('Stream not found');
          // Close without showing error
          onClose();
          return;
        }
        
        // Only show error for other types of errors
        showError('Failed to load live stream');
        onClose();
      }
      setLoading(false);
    };
    
    if (streamId) {
      loadStream();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId, user.token]);
  
  // Join stream
  const joinStream = async () => {
    try {
      const response = await axios.post(`/livestream/join/${streamId}`, {}, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      
      if (response.data.success) {
        setStream(response.data.liveStream);
        setJoined(true);
        // In a real implementation, you would connect to the actual video stream here
        // For now, we'll just show a placeholder
      }
    } catch (error) {
      console.error('Error joining stream:', error);
      
      // Handle 404 gracefully - stream has ended or doesn't exist
      if (error.response?.status === 404) {
        console.log('Stream not found or has ended');
        // Don't show error, just close the viewer
        onClose();
        return;
      }
      
      // Only show error for other types of errors
      showError('Failed to join live stream');
    }
  };
  
  // Leave stream. Single-flight guard: never run more than once per
  // mount, even if multiple effects/cleanups call it. This stops any
  // future bug from spamming /livestream/leave and exhausting the
  // browser's network handles.
  const leaveCalledRef = useRef(false);
  const leaveStream = async () => {
    if (leaveCalledRef.current) return;
    leaveCalledRef.current = true;
    try {
      await axios.post(`/livestream/leave/${streamId}`, {}, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
    } catch (error) {
      if (error.response?.status === 404) {
        console.log('Stream already ended or not found when leaving');
        return;
      }
      console.error('Error leaving stream:', error);
    }
  };

  const formatCommentTime = (createdAt) => {
    if (!createdAt) return '';
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleCommentSubmit = async () => {
    const text = newComment.trim();
    if (!text || sendingComment) return;

    try {
      setSendingComment(true);
      const response = await axios.post(`/livestream/comment/${streamId}`, {
        text,
      }, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      if (response.data?.success && response.data.comment) {
        const incoming = response.data.comment;
        setComments((prev) => {
          if (prev.some((c) => String(c._id) === String(incoming._id))) {
            return prev;
          }
          return [...prev, incoming];
        });
      }

      setNewComment('');
    } catch (error) {
      console.error('Error adding live stream comment:', error);
      showError(error.response?.data?.message || 'Failed to send comment');
    } finally {
      setSendingComment(false);
    }
  };

  // Confirmation modal state for exiting the stream (replaces browser confirm)
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Perform the actual cleanup and close actions. Kept as a separate function
  // so it can be invoked by the confirmation modal or directly when no
  // confirmation is needed.
  const performClose = async () => {
    setShowExitConfirm(false);
    setIsExiting(true);

    try {
      // Leave the stream if we were connected
      if (joined) {
        await leaveStream();
      }

      // Clean up connections
      if (peerRef.current) {
        peerRef.current.close();
        peerRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      const videoElement = videoRef.current;
      if (videoElement) {
        videoElement.srcObject = null;
      }

      // Call the original onClose
      onCloseRef.current?.();
    } catch (error) {
      console.error('Error while exiting stream:', error);
      setIsExiting(false);
    }
  };

  // Request close: if remote video is playing, show an in-app confirmation
  // modal instead of the browser-native confirm dialog. This prevents the
  // browser origin/title from appearing and provides a consistent app UI.
  const handleClose = () => {
    if (hasRemoteVideo) {
      setShowExitConfirm(true);
      return;
    }

    // No remote video playing; close immediately
    performClose();
  };
  
  // Diagnostic function to check connectivity
  const runConnectionDiagnostic = async () => {
    console.log('🔍 Running connection diagnostic...');
    
    try {
      // Test 1: Check backend connection
      console.log('1. Testing backend API connection...');
      try {
        const response = await axios.get(`/livestream/status/${streamId}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        console.log('✅ Backend API connection:', response.data);
        
        // Test 2: Check stream status
        if (response.data.success) {
          const { stream: streamInfo, serverStatus } = response.data;
          console.log('📊 Stream Status:', {
            isActive: streamInfo.isActive,
            hasHeartbeat: !!serverStatus.lastHeartbeat,
            roomExists: serverStatus.roomExists,
            hasBroadcaster: serverStatus.hasBroadcaster,
            broadcasterReady: serverStatus.broadcasterReady,
            viewerCount: serverStatus.viewerCount
          });
          
          if (!streamInfo.isActive) {
            throw new Error('Stream has ended');
          }
          
          if (!serverStatus.roomExists || !serverStatus.hasBroadcaster) {
            throw new Error('Broadcaster is not connected to the server');
          }
          
          if (!serverStatus.broadcasterReady) {
            throw new Error('Broadcaster is connecting, please wait a moment');
          }
        }
      } catch (error) {
        // Handle 404 - stream has ended
        if (error.response?.status === 404) {
          throw new Error('Stream has ended or does not exist');
        }
        throw error;
      }
      
      // Test 3: Check WebRTC support
      console.log('2. Testing WebRTC support...');
      if (!window.RTCPeerConnection) {
        throw new Error('Your browser does not support WebRTC');
      }
      console.log('✅ WebRTC is supported');
      
      // Test 4: Check getUserMedia (for debugging)
      console.log('3. Testing media access...');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: false });
        stream.getTracks().forEach(track => track.stop());
        console.log('✅ Media access available');
      } catch (e) {
        console.log('⚠️ Media access limited (not required for viewing)');
      }
      
      console.log('🎯 All checks passed! Attempting connection...');
      return true;
      
    } catch (error) {
      console.error('❌ Diagnostic failed:', error.message);
      showError(`Connection diagnostic failed: ${error.message}`);
      return false;
    }
  };

  // Register as a viewer immediately on open so WebRTC can start in
  // parallel with metadata loading (no longer blocked on GET /livestream).
  const joinAttemptedRef = useRef(false);
  useEffect(() => {
    if (!streamId || !user?.token || joinAttemptedRef.current) return;
    joinAttemptedRef.current = true;
    joinStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId, user?.token]);

  // Leave the stream once on unmount only.
  useEffect(() => {
    return () => {
      if (joinAttemptedRef.current) {
        leaveStream();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize WebRTC as soon as the viewer opens — do not wait for join
  // API or stream metadata (those run in parallel).
  useEffect(() => {
    if (!streamId || !currentUserId) return;

    const MAX_RETRIES = 5;
    const RETRY_DELAY = 2000; // 2 seconds
    let retryTimeout = null;
    let connectionTimeout = null;
    let requestStreamInterval = null;

    const initializeWebRTC = async () => {
      try {
        setIsConnecting(true);
        connectionRetryCount.current = 0; // Reset retry count
        retryCountRef.current = 0; // Reset broadcaster-availability retry
        if (retryCount !== 0) setRetryCount(0);
        console.log('🔌 Initializing WebRTC connection for stream:', streamId);

        // Proactively close any pre-existing peer to avoid leaking
        // RTCPeerConnections across re-renders (browser caps at ~500).
        if (peerRef.current) {
          try { peerRef.current.close(); } catch (_) {}
          peerRef.current = null;
        }
        pendingIceCandidatesRef.current = [];
        
        // Always use the existing notification socket if available
        let socket = null;
        if (window.notificationSocket && window.notificationSocket.connected) {
          socket = window.notificationSocket;
          console.log('✅ Using existing notification socket for viewing');
        } else {
          console.log('⚠️ No existing socket, creating new connection...');
          // Auto-detect backend URL: use env var if set, otherwise detect from current location
          let socketURL = process.env.REACT_APP_BACKEND_URL;
          
          if (!socketURL || socketURL === 'http://localhost:8000') {
            // In production, use same origin (relative URL) or detect from window
            if (typeof window !== 'undefined') {
              const hostname = window.location.hostname;
              
              // Check if it's a production domain (not localhost)
              if (hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.startsWith('192.168.')) {
                // Production: use same origin for socket (socket.io will handle it automatically)
                socketURL = '';
                // console.log(`🌐 Production domain detected: ${hostname}, using same origin for socket`);
              } else {
                // Development: use localhost:8000
                socketURL = 'http://localhost:8000';
                // console.log(`💻 Development mode: using ${socketURL} for socket`);
              }
            } else {
              socketURL = 'http://localhost:8000';
            }
          }
          
          socket = io(socketURL || undefined, {
            withCredentials: true,
            transports: ['websocket', 'polling'],
            timeout: 20000,
            forceNew: false, // Changed to false to prevent multiple connections
            reconnection: true,
            reconnectionAttempts: 3,
            reconnectionDelay: 1000
          });
          
          // Wait for connection
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('Socket connection timeout'));
            }, 10000);
            
            socket.on('connect', () => {
              clearTimeout(timeout);
              console.log('✅ New socket connected for viewing:', socket.id);
              resolve();
            });
            
            socket.on('connect_error', (error) => {
              clearTimeout(timeout);
              reject(error);
            });
          });
        }

        socketRef.current = socket;

        // Remove old handlers before attaching new ones to avoid duplicate offer handling.
        if (socketHandlersRef.current) {
          Object.entries(socketHandlersRef.current).forEach(([eventName, handler]) => {
            socket.off(eventName, handler);
          });
          socketHandlersRef.current = null;
        }

        const joinStreamRoom = () => {
          if (hasJoinedStreamRoomRef.current) return;
          if (!currentUserId) {
            showErrorRef.current?.('Unable to identify user for live stream connection. Please re-login and try again.');
            return;
          }

          hasJoinedStreamRoomRef.current = true;
          console.log('🔌 Joining stream room as viewer');
          socket.emit('join', String(currentUserId));
          socket.emit('join-stream', {
            streamId,
            userId: String(currentUserId),
            role: 'viewer'
          });

          // Request the stream immediately after join so that if the
          // broadcaster is already live, we don't wait an extra second.
          socket.emit('request-stream', {
            streamId,
            viewerId: String(currentUserId)
          });
        };

        // Create peer connection with better STUN/TURN configuration
        // Using multiple STUN servers for better NAT traversal
        peerRef.current = new RTCPeerConnection({
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            { urls: 'stun:stun2.l.google.com:19302' },
            { urls: 'stun:stun3.l.google.com:19302' },
            { urls: 'stun:stun4.l.google.com:19302' },
            // Add some additional STUN servers for better connectivity
            { urls: 'stun:stun.stunprotocol.org:3478' },
            { urls: 'stun:stun.voiparound.com' },
            { urls: 'stun:stun.voipbuster.com' }
          ],
          iceCandidatePoolSize: 10,
          iceTransportPolicy: 'all', // Try both relay and direct connections
          bundlePolicy: 'max-bundle',
          rtcpMuxPolicy: 'require'
        });

        // Handle incoming stream
        peerRef.current.ontrack = (event) => {
          console.log('📹 Received remote track:', event.track?.kind, 'streams:', event.streams?.length);
          handleRemoteTrack(event);
          if (connectionTimeout) clearTimeout(connectionTimeout);
        };

        // Handle connection state changes
        peerRef.current.onconnectionstatechange = () => {
          console.log('🔗 Connection state changed:', peerRef.current.connectionState);
          if (peerRef.current.connectionState === 'connected') {
            connectionRetryCount.current = 0;
            if (connectionTimeout) clearTimeout(connectionTimeout);
            console.log('✅ WebRTC connection established');
          } else if (peerRef.current.connectionState === 'failed') {
            console.log('❌ WebRTC connection failed');
            if (hasRemoteVideoRef.current) {
              setStreamConnected(false);
              setHasRemoteVideo(false);
              hasRemoteVideoRef.current = false;
            }
            
            // Try to restart ICE first
            try {
              console.log('🔄 Attempting to restart ICE...');
              peerRef.current.restartIce();
              
              // Wait a bit to see if restart works
              setTimeout(() => {
                if (peerRef.current && peerRef.current.connectionState === 'failed') {
                  // ICE restart didn't work, try recreating connection
                  if (connectionRetryCount.current < MAX_CONNECTION_RETRIES) {
                    connectionRetryCount.current++;
                    console.log(`🔄 Recreating peer connection (attempt ${connectionRetryCount.current}/${MAX_CONNECTION_RETRIES})...`);
                    
                    // Close existing connection
                    const oldPeer = peerRef.current;
                    oldPeer.close();
                    peerRef.current = null;
                    
                    // Recreate peer connection
                    setTimeout(() => {
                      if (socketRef.current && socketRef.current.connected) {
                        // Request stream again to trigger new connection
                        socketRef.current.emit('request-stream', { streamId, viewerId: String(currentUserId) });
                      }
                    }, 1000);
                  } else {
                    setIsConnecting(false);
                    console.error('❌ Max connection retries reached');
                    showErrorRef.current?.('Connection to live stream failed after multiple attempts. Please try refreshing the page.');
                  }
                }
              }, 5000);
            } catch (error) {
              console.error('Error restarting ICE:', error);
              setIsConnecting(false);
              showErrorRef.current?.('Connection to live stream failed. Please try again.');
            }
          } else if (peerRef.current.connectionState === 'disconnected') {
            console.log('⚠️ WebRTC connection disconnected');
            setStreamConnected(false);
            setHasRemoteVideo(false);
            hasRemoteVideoRef.current = false;
            // Don't show error immediately, wait to see if it reconnects
            setTimeout(() => {
              if (peerRef.current && peerRef.current.connectionState === 'disconnected') {
                setIsConnecting(false);
                showErrorRef.current?.('Connection to live stream lost. Please try again.');
              }
            }, 5000);
          }
        };

        // Handle ICE connection state changes
        peerRef.current.oniceconnectionstatechange = () => {
          console.log('🧊 ICE connection state:', peerRef.current.iceConnectionState);
          if (peerRef.current.iceConnectionState === 'failed') {
            console.log('❌ ICE connection failed, attempting to restart');
            try {
              peerRef.current.restartIce();
              // Give it time to recover
              setTimeout(() => {
                if (peerRef.current && peerRef.current.iceConnectionState === 'failed') {
                  console.log('❌ ICE restart did not recover connection');
                  // Connection state change handler will handle retry
                }
              }, 3000);
            } catch (error) {
              console.error('Error restarting ICE:', error);
            }
          } else if (peerRef.current.iceConnectionState === 'connected') {
            console.log('✅ ICE connection established');
            connectionRetryCount.current = 0;
            if (connectionTimeout) clearTimeout(connectionTimeout);
          } else if (peerRef.current.iceConnectionState === 'disconnected') {
            console.log('⚠️ ICE connection disconnected');
            setStreamConnected(false);
            setHasRemoteVideo(false);
            hasRemoteVideoRef.current = false;
          } else if (peerRef.current.iceConnectionState === 'checking') {
            console.log('🔄 ICE connection checking...');
            setIsConnecting(true);
          }
        };

        // Handle ICE gathering state changes
        peerRef.current.onicegatheringstatechange = () => {
          console.log('🧊 ICE gathering state:', peerRef.current.iceGatheringState);
        };

        // Handle ICE candidates
        peerRef.current.onicecandidate = (event) => {
          if (event.candidate && socketRef.current) {
            console.log('🧊 Sending ICE candidate to broadcaster');
            socketRef.current.emit('ice-candidate', {
              candidate: event.candidate,
              streamId,
              viewerId: String(currentUserId),
              type: 'viewer'
            });
          }
        };

        // Join stream room as viewer
        const handleConnect = () => {
          console.log('🔌 Socket connected');
          joinStreamRoom();
        };
        socket.on('connect', handleConnect);

        // If reusing an already connected socket, join immediately.
        if (socket.connected) {
          joinStreamRoom();
        }

        // Handle broadcaster ready event
        const handleBroadcasterReady = (data) => {
          console.log('📡 Broadcaster is ready, requesting stream');
          socket.emit('request-stream', { streamId, viewerId: String(currentUserId) });
        };
        socket.on('broadcaster-ready', handleBroadcasterReady);

        // Handle no broadcaster available - retry logic.
        // Use a ref-backed counter so retries don't trigger this entire
        // effect to re-run (which would create new RTCPeerConnections).
        const handleNoBroadcaster = (data) => {
          const currentRetry = retryCountRef.current;
          console.log('❌ No broadcaster available, retry count:', currentRetry);
          if (currentRetry < MAX_RETRIES) {
            retryTimeout = setTimeout(() => {
              retryCountRef.current = currentRetry + 1;
              setRetryCount(currentRetry + 1);
              console.log('🔄 Retrying stream request...');
              if (socket.connected) {
                socket.emit('request-stream', { streamId, viewerId: String(currentUserId) });
              }
            }, RETRY_DELAY);
          } else {
            setIsConnecting(false);
            console.log('❌ Max retries reached, giving up');
            showErrorRef.current?.('Unable to connect to the live stream after several attempts. The broadcaster may be offline or experiencing issues.');
          }
        };
        socket.on('no-broadcaster', handleNoBroadcaster);

        const handleStreamOffer = async (data) => {
          console.log('📡 Received stream offer from broadcaster');
          const incomingOffer = data?.offer;
          if (!incomingOffer || incomingOffer.type !== 'offer') {
            console.warn('⚠️ Ignoring invalid stream-offer payload');
            return;
          }

          if (isHandlingOfferRef.current) {
            console.log('⚠️ Offer handling already in progress, ignoring duplicate offer');
            return;
          }

          try {
            isHandlingOfferRef.current = true;

            if (!peerRef.current) {
              console.log('❌ No peer connection available, creating new one');
              pendingIceCandidatesRef.current = [];
              // Create a new peer connection if it doesn't exist
              peerRef.current = new RTCPeerConnection({
                iceServers: [
                  { urls: 'stun:stun.l.google.com:19302' },
                  { urls: 'stun:stun1.l.google.com:19302' },
                  { urls: 'stun:stun2.l.google.com:19302' },
                  { urls: 'stun:stun3.l.google.com:19302' },
                  { urls: 'stun:stun4.l.google.com:19302' }
                ],
                iceCandidatePoolSize: 10
              });

              // Re-setup event handlers
              peerRef.current.ontrack = (event) => {
                console.log('📹 Received remote track (new peer):', event.track?.kind);
                handleRemoteTrack(event);
                if (connectionTimeout) clearTimeout(connectionTimeout);
              };

              peerRef.current.onconnectionstatechange = () => {
                console.log('🔗 Connection state changed:', peerRef.current.connectionState);
                if (peerRef.current.connectionState === 'connected') {
                  if (connectionTimeout) clearTimeout(connectionTimeout);
                  console.log('✅ WebRTC connection established');
                } else if (peerRef.current.connectionState === 'failed') {
                  setStreamConnected(false);
                  setHasRemoteVideo(false);
                  hasRemoteVideoRef.current = false;
                  setIsConnecting(false);
                  console.log('❌ WebRTC connection failed, attempting to restart ICE');
                  try {
                    peerRef.current.restartIce();
                  } catch (error) {
                    console.error('Error restarting ICE:', error);
                  }
                }
              };

              peerRef.current.oniceconnectionstatechange = () => {
                console.log('🧊 ICE connection state:', peerRef.current.iceConnectionState);
                if (peerRef.current.iceConnectionState === 'failed') {
                  console.log('❌ ICE connection failed, attempting to restart');
                  try {
                    peerRef.current.restartIce();
                  } catch (error) {
                    console.error('Error restarting ICE:', error);
                  }
                } else if (peerRef.current.iceConnectionState === 'connected') {
                  console.log('✅ ICE connection established');
                  if (connectionTimeout) clearTimeout(connectionTimeout);
                }
              };

              peerRef.current.onicecandidate = (event) => {
                if (event.candidate && socketRef.current) {
                  console.log('🧊 Sending ICE candidate to broadcaster');
                  socketRef.current.emit('ice-candidate', {
                    candidate: event.candidate,
                    streamId,
                    viewerId: String(currentUserId),
                    type: 'viewer'
                  });
                }
              };
            }

            const peer = peerRef.current;
            const incomingSdp = incomingOffer.sdp || '';

            // Ignore exact duplicate offers once negotiation is complete.
            if (
              incomingSdp &&
              incomingSdp === lastHandledOfferSdpRef.current &&
              peer.currentRemoteDescription &&
              peer.currentLocalDescription &&
              peer.signalingState === 'stable'
            ) {
              console.log('⚠️ Duplicate offer received after negotiation, ignoring');
              return;
            }

            // Prevent answer creation in invalid states (causes stable-state errors).
            if (!['stable', 'have-remote-offer'].includes(peer.signalingState)) {
              console.warn('⚠️ Ignoring offer in signaling state:', peer.signalingState);
              return;
            }

            if (!peer.currentRemoteDescription || peer.currentRemoteDescription.sdp !== incomingSdp) {
              await peer.setRemoteDescription(incomingOffer);
              await drainIceCandidateQueue(peer);
            }
            console.log('✅ Remote description set');

            if (peer.signalingState !== 'have-remote-offer') {
              console.warn('⚠️ Peer not ready to create answer, state:', peer.signalingState);
              return;
            }

            const answer = await peer.createAnswer();
            await peer.setLocalDescription(answer);
            console.log('✅ Local description set, sending answer');

            lastHandledOfferSdpRef.current = incomingSdp;
            
            if (socket.connected) {
              socket.emit('stream-answer', {
                answer,
                streamId,
                viewerId: String(currentUserId)
              });
              console.log('✅ Answer sent to broadcaster');
            } else {
              console.error('❌ Socket not connected, cannot send answer');
            }
          } catch (error) {
            console.error('❌ Error handling stream offer:', error);
            setIsConnecting(false);
            showErrorRef.current?.('Failed to connect to live stream: ' + error.message);
          } finally {
            isHandlingOfferRef.current = false;
          }
        };
        socket.on('stream-offer', handleStreamOffer);

        const handleIceCandidate = async (data) => {
          if (!data?.candidate || !peerRef.current) return;
          await addRemoteIceCandidate(peerRef.current, data.candidate);
        };
        socket.on('ice-candidate', handleIceCandidate);

        const handleLiveStreamComment = (data) => {
          if (!data || data.streamId !== streamId || !data.comment) return;
          setComments((prev) => {
            if (prev.some((c) => String(c._id) === String(data.comment._id))) {
              return prev;
            }
            return [...prev, data.comment];
          });
        };

        socket.on('live-stream-comment', handleLiveStreamComment);

        const handleStreamEnded = () => {
          console.log('📡 Stream ended by broadcaster');
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
          setStreamConnected(false);
          setHasRemoteVideo(false);
          hasRemoteVideoRef.current = false;
          setIsConnecting(false);
          // Don't show error when stream ends normally - just close
          onCloseRef.current?.();
        };
        socket.on('stream-ended', handleStreamEnded);

        const handleViewerCountUpdated = (data) => {
          if (!data || String(data.streamId) !== String(streamId)) return;
          setStream(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              viewerCount: typeof data.viewerCount === 'number' ? data.viewerCount : prev.viewerCount,
            };
          });
        };
        socket.on('viewerCountUpdated', handleViewerCountUpdated);

        const handleConnectError = (error) => {
          console.error('❌ Socket connection error:', error);
          setIsConnecting(false);
          showErrorRef.current?.('Connection error. Please check your internet connection.');
        };
        socket.on('connect_error', handleConnectError);

        socketHandlersRef.current = {
          connect: handleConnect,
          'broadcaster-ready': handleBroadcasterReady,
          'no-broadcaster': handleNoBroadcaster,
          'stream-offer': handleStreamOffer,
          'ice-candidate': handleIceCandidate,
          'stream-ended': handleStreamEnded,
          'viewerCountUpdated': handleViewerCountUpdated,
          'connect_error': handleConnectError,
        };

        // Set connection timeout (use ref — state in this closure is stale)
        connectionTimeout = setTimeout(() => {
          if (!hasRemoteVideoRef.current) {
            console.log('⏰ Connection timeout reached');
            setIsConnecting(false);
            showErrorRef.current?.('Connection timeout. The broadcaster may be unavailable.');
          }
        }, 30000); // 30 second timeout

        // Initial request for stream - fire immediately if socket is
        // already connected (joinStreamRoom also requests when joining).
        if (socket.connected) {
          console.log('📡 Requesting stream from broadcaster');
          socket.emit('request-stream', { streamId, viewerId: String(currentUserId) });
        }

        // Keep nudging stream request while connecting, so viewers don't
        // wait for long fallback timers when one socket event is missed.
        requestStreamInterval = setInterval(() => {
          if (!socket.connected || hasRemoteVideoRef.current) return;
          socket.emit('request-stream', { streamId, viewerId: String(currentUserId) });
        }, 800);

        return () => {
          if (retryTimeout) clearTimeout(retryTimeout);
          if (connectionTimeout) clearTimeout(connectionTimeout);
          if (requestStreamInterval) clearInterval(requestStreamInterval);
        };

      } catch (error) {
        console.error('❌ Error initializing WebRTC:', error);
        setIsConnecting(false);
        showErrorRef.current?.('Failed to initialize connection: ' + error.message);
      }
    };

    initializeWebRTC();

    // Cleanup on unmount
    const videoElement = videoRef.current;
    return () => {
      hasJoinedStreamRoomRef.current = false;

      if (socketRef.current && socketHandlersRef.current) {
        Object.entries(socketHandlersRef.current).forEach(([eventName, handler]) => {
          socketRef.current.off(eventName, handler);
        });
        socketHandlersRef.current = null;
      }

      if (retryTimeout) clearTimeout(retryTimeout);
      if (connectionTimeout) clearTimeout(connectionTimeout);
      if (requestStreamInterval) clearInterval(requestStreamInterval);
      if (peerRef.current) {
        peerRef.current.close();
        peerRef.current = null;
      }
      if (socketRef.current && !window.notificationSocket) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (videoElement) {
        videoElement.srcObject = null;
      }
      if (socketRef.current) {
        socketRef.current.off('live-stream-comment');
      }
      isHandlingOfferRef.current = false;
      lastHandledOfferSdpRef.current = '';
      pendingIceCandidatesRef.current = [];
      hasRemoteVideoRef.current = false;
      setHasRemoteVideo(false);
      setStreamConnected(false);
      setIsConnecting(false);
    };
    // IMPORTANT: keep this dep array minimal. Including `stream`,
    // `retryCount`, or `showError` here would re-run this effect on every
    // viewerCountUpdated / retry / re-render and leak RTCPeerConnections,
    // eventually triggering "Cannot create so many PeerConnections".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId, currentUserId, webrtcSessionKey]);
  
  if (!loading && !stream) {
    return (
      <div className="live_stream_overlay">
        <div className="live_stream_modal">
          <div className="error_container">
            <p>Live stream not found or has ended</p>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="live_stream_overlay">
      <div className="live_stream_modal">
        <div className="live_stream_header">
          <div className="stream_info">
            <div className="live_indicator">🔴 LIVE</div>
            <div className="stream_title">{stream?.title || 'Live stream'}</div>
            <div className="stream_duration">⏱️ {streamDuration}</div>
          </div>
          <button className="close_btn" onClick={handleClose}>
            ✕
          </button>
        </div>
        
        <div className="live_stream_content">
          {/* Video Stream */}
          <div className="video_container" style={{ position: 'relative' }}>
            {/* Real Video Element for Live Stream */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              controls={false}
              muted={isMuted}
              className="live_video_element"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                backgroundColor: '#000',
                borderRadius: '8px',
                opacity: hasRemoteVideo ? 1 : 0,
                transition: 'opacity 0.2s ease',
              }}
              onLoadedData={() => markVideoReady()}
              onPlaying={() => markVideoReady()}
              onLoadStart={() => console.log('Video loading started')}
              onCanPlay={() => console.log('Video can play')}
              onError={(e) => console.error('Video error:', e)}
            />

            {/* User-gesture overlay shown only if autoplay was blocked.
                Clicking it satisfies the browser's autoplay policy. */}
            {needsUserGestureToPlay && !hasRemoteVideo && (
              <button
                type="button"
                onClick={handleManualPlay}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0, 0, 0, 0.55)',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  zIndex: 3,
                }}
              >
                <span style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  ▶ Tap to play
                </span>
              </button>
            )}

            {/* Unmute / mute toggle. Stream starts muted to satisfy
                browser autoplay policies; one tap turns audio on. */}
            {hasRemoteVideo && !needsUserGestureToPlay && (
              <button
                type="button"
                onClick={toggleMute}
                aria-label={isMuted ? 'Unmute live stream' : 'Mute live stream'}
                style={{
                  position: 'absolute',
                  bottom: '12px',
                  right: '12px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                }}
              >
                {isMuted ? '🔇' : '🔊'}
              </button>
            )}
            
                         {/* Fallback placeholder when video is not available */}
             <div 
               className="stream_placeholder"
               style={{
                 position: 'absolute',
                 top: 0,
                 left: 0,
                 width: '100%',
                 height: '100%',
                 display: hasRemoteVideo ? 'none' : 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                 borderRadius: '8px'
               }}
             >
               <div className="placeholder_content">
                 <div className="placeholder_text" style={{ textAlign: 'center', color: 'white' }}>
                   <h3>🎥 {stream?.user?.first_name || 'Someone'} {stream?.user?.last_name || ''} is Live</h3>
                   <p>"{stream?.title || 'Live stream'}"</p>
                   <div style={{
                     background: 'rgba(255,255,255,0.2)',
                     padding: '15px',
                     borderRadius: '8px',
                     marginTop: '20px'
                   }}>
                     {isConnecting ? (
                       <>
                         <p style={{fontSize: '14px', marginBottom: '10px'}}>
                           🔄 Connecting to live stream...
                         </p>
                         <div style={{
                           display: 'inline-block',
                           width: '20px',
                           height: '20px',
                           border: '2px solid rgba(255,255,255,0.3)',
                           borderTop: '2px solid white',
                           borderRadius: '50%',
                           animation: 'spin 1s linear infinite'
                         }}></div>
                       </>
                     ) : (
                       <>
                         <p style={{fontSize: '14px', marginBottom: '10px', textAlign: 'center'}}>
                           📡 Connection Failed
                         </p>
                         <p style={{fontSize: '12px', opacity: '0.9', textAlign: 'center', marginBottom: '15px'}}>
                           Unable to connect to the live stream. The broadcaster may have connectivity issues or the stream may have ended.
                         </p>
                         <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                           <button
                             onClick={async () => {
                               console.log('🔄 Retrying connection...');
                               setIsConnecting(true);
                               setStreamConnected(false);
                               setHasRemoteVideo(false);
                               hasRemoteVideoRef.current = false;
                               
                               // Run diagnostic first
                               const diagnosticPassed = await runConnectionDiagnostic();
                               
                               if (diagnosticPassed) {
                                 if (peerRef.current) {
                                   peerRef.current.close();
                                   peerRef.current = null;
                                 }
                                 // Keep the shared notification socket alive; only
                                 // bump the session key to re-run WebRTC setup.
                                 hasJoinedStreamRoomRef.current = false;
                                 setTimeout(() => {
                                   setWebrtcSessionKey((k) => k + 1);
                                 }, 500);
                               } else {
                                 setIsConnecting(false);
                               }
                             }}
                             style={{
                               padding: '10px 20px',
                               backgroundColor: '#1876f2',
                               border: 'none',
                               borderRadius: '6px',
                               color: 'white',
                               cursor: 'pointer',
                               fontSize: '13px',
                               fontWeight: '500'
                             }}
                           >
                             🔄 Try Again
                           </button>
                           <button
                             onClick={handleClose}
                             style={{
                               padding: '8px 16px',
                               backgroundColor: 'rgba(255,255,255,0.1)',
                               border: '1px solid rgba(255,255,255,0.2)',
                               borderRadius: '4px',
                               color: 'white',
                               cursor: 'pointer',
                               fontSize: '12px'
                             }}
                                                        >
                               Close
                             </button>

                             <button
                               onClick={() => setShowTestConnection(true)}
                               style={{
                                 padding: '6px 12px',
                                 backgroundColor: 'rgba(255,255,255,0.1)',
                                 border: '1px solid rgba(255,255,255,0.2)',
                                 borderRadius: '4px',
                                 color: 'white',
                                 cursor: 'pointer',
                                 fontSize: '11px'
                               }}
                             >
                               🔧 Test Connection
                             </button>
                         </div>

                       </>
                     )}
                     {stream?.description && (
                       <p style={{fontSize: '12px', marginTop: '10px', fontStyle: 'italic'}}>
                         "{stream.description}"
                       </p>
                                 )}
          </div>
          {hasRemoteVideo && (
            <div style={{
              textAlign: 'center',
              fontSize: '12px',
              color: 'var(--color-secondary)',
              marginTop: '10px',
              opacity: 0.7
            }}>
              💡 Tip: Press <kbd style={{
                background: 'var(--bg-third)',
                padding: '2px 6px',
                borderRadius: '3px',
                fontSize: '11px',
                fontFamily: 'monospace'
              }}>Esc</kbd> to exit quickly
            </div>
          )}
        </div>
      </div>
             </div>
            
            <div className="stream_overlay_info">
              <div style={{
                background: 'rgba(255, 0, 0, 0.8)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold',
                animation: 'pulse 2s infinite'
              }}>
                🔴 LIVE
              </div>
            </div>
          </div>
          
          {/* Streamer Info */}
          <div className="streamer_info">
            <img 
              src={stream?.user?.picture || '/images/default_pic.png'} 
              alt={`${stream?.user?.first_name || ''} ${stream?.user?.last_name || ''}`}
              className="streamer_avatar"
            />
            <div className="streamer_details">
              <div className="streamer_name">
                {stream?.user?.first_name} {stream?.user?.last_name}
              </div>
              <div className="stream_description">
                {stream?.description || 'No description provided'}
              </div>
            </div>
          </div>

          {stream?.settings?.allowComments !== false && (
            <div className="live_comments_section">
              <div className="live_comments_title">Live Comments</div>
              <div className="live_comments_list">
                {comments.length === 0 ? (
                  <div className="live_comment_empty">Be the first to comment...</div>
                ) : (
                  comments.map((comment) => (
                    <div key={comment._id || `${comment.user?._id}-${comment.createdAt}`} className="live_comment_item">
                      <img
                        src={comment.user?.picture || '/images/default_pic.png'}
                        alt={comment.user?.first_name || 'user'}
                        className="live_comment_avatar"
                      />
                      <div className="live_comment_body">
                        <div className="live_comment_header">
                          <span className="live_comment_name">
                            {comment.user?.first_name || ''} {comment.user?.last_name || ''}
                          </span>
                          <span className="live_comment_time">{formatCommentTime(comment.createdAt)}</span>
                        </div>
                        <div className="live_comment_text">{comment.text}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="live_comment_input_row">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  maxLength={500}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCommentSubmit();
                    }
                  }}
                  className="live_comment_input"
                />
                <button
                  onClick={handleCommentSubmit}
                  disabled={sendingComment || !newComment.trim()}
                  className="live_comment_send_btn"
                >
                  {sendingComment ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          )}
          
          {/* Stream Actions */}
          <div className="stream_actions">
            <div className="action_info">
              <span className="viewers_text">
                {stream?.viewerCount || 0} people watching
              </span>
            </div>
            {hasRemoteVideo && (
              <div className="stream_control_actions">
                <button 
                  className="cancel_stream_btn"
                  onClick={handleClose}
                  disabled={isExiting}
                  title="Press Escape key to exit quickly"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: isExiting ? '#95a5a6' : '#e74c3c',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    cursor: isExiting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isExiting ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Exiting...
                    </>
                  ) : (
                    '🚪 Exit Stream'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {showTestConnection && (
        <TestConnection
          streamId={streamId}
          user={user}
          onClose={() => setShowTestConnection(false)}
        />
      )}
      {showExitConfirm && (
        <div
          className="lsv_exit_confirm_overlay"
          onClick={() => setShowExitConfirm(false)}
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.45)',
            zIndex: 12000,
            padding: '20px'
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '420px',
              background: 'var(--bg-primary, #fff)',
              borderRadius: '12px',
              boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
              padding: '18px',
              color: 'var(--color-primary, #111)'
            }}
          >
              <h3 style={{ margin: 0, fontSize: '18px' }}>Exit Live Stream?</h3>
              <p style={{ margin: 0, color: 'var(--color-secondary, #6b7280)', fontSize: '14px' }}>
                Are you sure you want to leave the live stream? You can rejoin anytime while the stream is active.
              </p>

              <div style={{ display: 'flex', gap: '10px', marginTop: '16px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowExitConfirm(false)}
                  style={{
                    padding: '8px 14px',
                    background: 'transparent',
                    border: '1px solid var(--border-primary, #d1d5db)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                  disabled={isExiting}
                >
                  Stay
                </button>
                <button
                  onClick={() => performClose()}
                  style={{
                    padding: '8px 14px',
                    background: isExiting ? '#95a5a6' : '#e74c3c',
                    border: 'none',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: isExiting ? 'not-allowed' : 'pointer'
                  }}
                  disabled={isExiting}
                >
                  {isExiting ? 'Exiting...' : 'Exit'}
                </button>
              </div>
            
          </div>
        </div>
      )}
    </div>
  );
} 
